import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useLocation } from '../pages/Tasks/hooks/useLocation';

const AttendanceSettingsModal = ({
  isOpen,
  onClose,
  colors,
  departments = [],
  selectedDepartmentId = 'company',
  onChangeDepartment,
  settingsDraft,
  setSettingsDraft,
  isLoading = false,
  isSaving = false,
  error = '',
  onSave,
  onReset,
  isDirty = false,
  isPersisted = false,
  summariesLoading = false,
  summaries = {}
}) => {
  const [geofenceMapKey, setGeofenceMapKey] = useState(0);

  const {
    isLoadingLocation: isGeofenceLoading,
    locationError: geofenceLocationError,
    locationSuggestions: geofenceSuggestions,
    showSuggestions: geofenceShowSuggestions,
    suggestionsRef: geofenceSuggestionsRef,
    searchPlaces: geofenceSearchPlaces,
    selectSuggestion: geofenceSelectSuggestion,
    reverseGeocode: geofenceReverseGeocode,
    setLocationError: setGeofenceLocationError,
    isProcessingUrl: isGeofenceProcessingUrl,
    isGoogleMapsUrl: isGeofenceGoogleMapsUrl,
    processGoogleMapsUrl: processGeofenceGoogleMapsUrl,
    getCurrentLocationWithAddress: getGeofenceCurrentLocationWithAddress
  } = useLocation();

  // Fix for default marker icons in Leaflet
  try {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
    });
  } catch {}

  // Keep geofence map re-centered when coordinates change
  useEffect(() => {
    const lat = settingsDraft?.geofencingLocation?.coordinates?.lat;
    const lng = settingsDraft?.geofencingLocation?.coordinates?.lng;
    if (lat != null && lng != null) setGeofenceMapKey(k => k + 1);
  }, [settingsDraft?.geofencingLocation?.coordinates?.lat, settingsDraft?.geofencingLocation?.coordinates?.lng]);

  if (!isOpen) return null;

  const dayLabels = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ];

  const toggleWorkingDay = (k) => {
    setSettingsDraft(prev => {
      const set = new Set(prev.workingDays || []);
      if (set.has(k)) set.delete(k);
      else set.add(k);
      return { ...prev, workingDays: Array.from(set) };
    });
  };

  const disabled = isLoading || isSaving;

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    fontSize: 13,
    outline: 'none'
  };

  const labelStyle = { fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 };

  const Toggle = ({ value, onChange, label, description }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'center', padding: '12px 12px', border: `1px solid ${colors.border}`, borderRadius: 12, background: '#fff' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>{label}</div>
        {!!description && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{description}</div>}
      </div>
      <button
        onClick={onChange}
        style={{
          width: 44,
          height: 26,
          borderRadius: 999,
          border: `1px solid ${value ? colors.today : colors.border}`,
          background: value ? colors.today : '#fff',
          padding: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: value ? 'flex-end' : 'flex-start',
          flexShrink: 0
        }}
      >
        <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }} />
      </button>
    </div>
  );

  const GeofenceClickSetter = ({ enabled }) => {
    useMapEvents({
      click: (e) => {
        if (!enabled) return;
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        setSettingsDraft(prev => ({
          ...prev,
          geofencingLocation: {
            ...(prev.geofencingLocation || {}),
            coordinates: { lat, lng }
          }
        }));
        geofenceReverseGeocode({ lat, lng }).then((r) => {
          if (r?.success && r.address) {
            setSettingsDraft(prev => ({
              ...prev,
              geofencingLocation: {
                ...(prev.geofencingLocation || {}),
                address: r.address,
                coordinates: { lat, lng }
              }
            }));
          }
        }).catch(() => {});
      }
    });
    return null;
  };

  const lat = settingsDraft?.geofencingLocation?.coordinates?.lat;
  const lng = settingsDraft?.geofencingLocation?.coordinates?.lng;
  const hasCoords = lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
  const mapCenter = hasCoords ? [Number(lat), Number(lng)] : [20.5937, 78.9629]; // default to India

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 20
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: 18,
          maxWidth: 760,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px -15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${colors.border}`, background: '#fff', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: colors.text }}>Attendance Settings</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
              Company default + department overrides
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={disabled}
            style={{ border: 'none', background: colors.weekend, color: colors.textMuted, padding: '8px 12px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 12, opacity: disabled ? 0.7 : 1 }}
          >
            Close
          </button>
        </div>

        <div style={{ padding: 20, overflow: 'auto' }}>
          {/* Department cards */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
              Department
            </div>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6 }}>
              {['company', ...(departments || []).map(d => String(d.id))].map((key) => {
                const isSelected = String(selectedDepartmentId || 'company') === String(key);
                const deptName = key === 'company'
                  ? 'Company default'
                  : (departments.find(d => String(d.id) === String(key))?.name || `Dept ${key}`);
                const info = summaries?.[key];
                const exists = info?.exists === true;
                const draft = info?.draft || null;
                const summaryLine = draft
                  ? `${draft.workStartTime}-${draft.workEndTime} • ${Array.isArray(draft.workingDays) ? draft.workingDays.length : 0} days`
                  : 'Not configured';
                const subLine = draft
                  ? `${draft.requireLocationForAttendance ? 'Loc: required' : 'Loc: optional'} • ${draft.geofencingEnabled ? `Geo: on (${draft.geofencingRadius}m)` : 'Geo: off'}`
                  : 'Uses defaults until saved';
                return (
                  <button
                    key={key}
                    onClick={() => onChangeDepartment?.(key)}
                    disabled={disabled}
                    style={{
                      minWidth: 210,
                      textAlign: 'left',
                      borderRadius: 14,
                      border: isSelected ? `2px solid ${colors.today}` : `1px solid ${colors.border}`,
                      background: isSelected ? colors.todayBg : '#fff',
                      padding: 12,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.7 : 1
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {deptName}
                      </div>
                      <div style={{
                        fontSize: 10,
                        fontWeight: 900,
                        padding: '3px 8px',
                        borderRadius: 999,
                        background: exists ? colors.presentBg : colors.weekend,
                        color: exists ? colors.present : colors.textMuted,
                        border: `1px solid ${colors.border}`,
                        flexShrink: 0
                      }}>
                        {summariesLoading ? '…' : (exists ? 'Set' : 'New')}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 6 }}>
                      {summaryLine}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                      {subLine}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {(isLoading || error) && (
            <div style={{
              border: `1px solid ${colors.border}`,
              background: '#fff',
              borderRadius: 12,
              padding: 12,
              marginBottom: 12,
              fontSize: 12,
              color: error ? colors.absent : colors.textMuted
            }}>
              {error ? error : 'Loading settings…'}
            </div>
          )}
          {/* Work schedule */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: colors.text, marginBottom: 10 }}>Work schedule</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
              <div>
                <div style={labelStyle}>Start time</div>
                <input type="time" value={settingsDraft.workStartTime || ''} onChange={(e) => setSettingsDraft(prev => ({ ...prev, workStartTime: e.target.value }))} style={inputStyle} disabled={disabled} />
              </div>
              <div>
                <div style={labelStyle}>End time</div>
                <input type="time" value={settingsDraft.workEndTime || ''} onChange={(e) => setSettingsDraft(prev => ({ ...prev, workEndTime: e.target.value }))} style={inputStyle} disabled={disabled} />
              </div>
              <div>
                <div style={labelStyle}>Lunch start</div>
                <input type="time" value={settingsDraft.lunchBreakStart || ''} onChange={(e) => setSettingsDraft(prev => ({ ...prev, lunchBreakStart: e.target.value }))} style={inputStyle} disabled={disabled} />
              </div>
              <div>
                <div style={labelStyle}>Lunch end</div>
                <input type="time" value={settingsDraft.lunchBreakEnd || ''} onChange={(e) => setSettingsDraft(prev => ({ ...prev, lunchBreakEnd: e.target.value }))} style={inputStyle} disabled={disabled} />
              </div>
              <div>
                <div style={labelStyle}>Grace (minutes)</div>
                <input
                  type="number"
                  min={0}
                  value={settingsDraft.graceTimeInMinutes ?? 0}
                  onChange={(e) => setSettingsDraft(prev => ({ ...prev, graceTimeInMinutes: Number(e.target.value) }))}
                  style={inputStyle}
                  disabled={disabled}
                />
              </div>
              <div>
                <div style={labelStyle}>Working days</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {dayLabels.map(d => {
                    const active = (settingsDraft.workingDays || []).includes(d.key);
                    return (
                      <button
                        key={d.key}
                        onClick={() => toggleWorkingDay(d.key)}
                        disabled={disabled}
                        style={{
                          border: `1px solid ${active ? colors.today : colors.border}`,
                          background: active ? colors.todayBg : '#fff',
                          color: active ? colors.today : colors.textMuted,
                          padding: '8px 10px',
                          borderRadius: 10,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          fontSize: 12,
                          fontWeight: 800
                        }}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Approval */}
          {/* <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: colors.text, marginBottom: 10 }}>Approvals</div>
            <Toggle
              value={!!settingsDraft.requireApproval}
              onChange={() => setSettingsDraft(prev => ({ ...prev, requireApproval: !prev.requireApproval }))}
              label="Require approval"
              description="When enabled, attendance records may need supervisor/admin approval."
            />
          </div> */}

          {/* Location */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: colors.text, marginBottom: 10 }}>Location & geofencing</div>
            <div style={{ display: 'grid', gap: 12 }}>
             {/*  <Toggle
                value={!!settingsDraft.requireLocationForAttendance}
                onChange={() => setSettingsDraft(prev => ({ ...prev, requireLocationForAttendance: !prev.requireLocationForAttendance }))}
                label="Require location for attendance"
                description="If enabled, check-in/out should capture GPS location."
              /> */}
              <Toggle
                value={!!settingsDraft.geofencingEnabled}
                onChange={() => setSettingsDraft(prev => ({ ...prev, geofencingEnabled: !prev.geofencingEnabled }))}
                label="Enable geofencing"
                description="Set an office location + radius and use it to flag distance from company."
              />

              {/* Search + map picker */}
              {settingsDraft.geofencingEnabled && (
                <div style={{ border: `1px solid ${colors.border}`, borderRadius: 14, background: '#fff', padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: colors.text }}>Office location picker</div>
                      <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                        Search an address, then fine-tune by dragging the pin (or click the map).
                      </div>
                    </div>
                  </div>

                  <div style={{ position: 'relative' }} ref={geofenceSuggestionsRef}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="Enter location or paste Google Maps URL"
                        value={settingsDraft.geofencingLocation?.address || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSettingsDraft(prev => ({
                            ...prev,
                            geofencingLocation: {
                              ...(prev.geofencingLocation || {}),
                              address: value
                            }
                          }));
                          if (value && geofenceLocationError) setGeofenceLocationError('');
                          geofenceSearchPlaces(value);
                        }}
                        onPaste={async (e) => {
                          const pastedText = e.clipboardData.getData('text');
                          if (isGeofenceGoogleMapsUrl(pastedText)) {
                            e.preventDefault();
                            const result = await processGeofenceGoogleMapsUrl(pastedText);
                            if (result?.success && result.location) {
                              setSettingsDraft(prev => ({
                                ...prev,
                                geofencingLocation: result.location
                              }));
                            } else {
                              setGeofenceLocationError(result?.error || 'Could not process Google Maps URL');
                              setSettingsDraft(prev => ({
                                ...prev,
                                geofencingLocation: { ...(prev.geofencingLocation || {}), address: pastedText }
                              }));
                            }
                          }
                        }}
                        style={{ ...inputStyle, flex: 1 }}
                        disabled={!settingsDraft.geofencingEnabled || isGeofenceProcessingUrl}
                      />
                      <button
                        onClick={async () => {
                          const r = await getGeofenceCurrentLocationWithAddress();
                          if (r?.success && r.location) {
                            setSettingsDraft(prev => ({ ...prev, geofencingLocation: r.location }));
                            if (r.warning) setGeofenceLocationError(r.warning);
                          } else if (r?.error) {
                            setGeofenceLocationError(r.error);
                          }
                        }}
                        disabled={!settingsDraft.geofencingEnabled || isGeofenceLoading || isGeofenceProcessingUrl}
                        style={{
                          border: `1px solid ${colors.border}`,
                          background: '#fff',
                          color: colors.textMuted,
                          padding: '10px 12px',
                          borderRadius: 10,
                          cursor: settingsDraft.geofencingEnabled ? 'pointer' : 'not-allowed',
                          fontWeight: 900,
                          fontSize: 12,
                          opacity: (isGeofenceLoading || isGeofenceProcessingUrl) ? 0.7 : 1
                        }}
                        title="Use current location"
                      >
                        {isGeofenceLoading || isGeofenceProcessingUrl ? 'Loading…' : 'Use my location'}
                      </button>
                    </div>

                    {geofenceShowSuggestions && geofenceSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: 46,
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: `1px solid ${colors.border}`,
                        borderRadius: 12,
                        overflow: 'hidden',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
                        zIndex: 5
                      }}>
                        {geofenceSuggestions.map((s, idx) => (
                          <button
                            key={`${s.place_id || idx}-${idx}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              const loc = geofenceSelectSuggestion(s);
                              setSettingsDraft(prev => ({ ...prev, geofencingLocation: loc }));
                            }}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '10px 12px',
                              border: 'none',
                              background: '#fff',
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 700, color: colors.text, lineHeight: 1.25 }}>
                              {s.display_name}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {!!(geofenceLocationError) && <div style={{ marginTop: 8, fontSize: 12, color: colors.absent }}>{geofenceLocationError}</div>}

                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 10 }}>
                      <div>
                        <div style={labelStyle}>Radius (meters)</div>
                        <input
                          type="number"
                          min={0}
                          value={settingsDraft.geofencingRadius ?? 0}
                          onChange={(e) => setSettingsDraft(prev => ({ ...prev, geofencingRadius: Number(e.target.value) }))}
                          style={inputStyle}
                          disabled={!settingsDraft.geofencingEnabled}
                        />
                      </div>
                      <div>
                        <div style={labelStyle}>Latitude</div>
                        <input
                          type="number"
                          placeholder="e.g. 12.9716"
                          value={settingsDraft.geofencingLocation?.coordinates?.lat ?? ''}
                          onChange={(e) => {
                            const nextLat = e.target.value === '' ? null : Number(e.target.value);
                            const curLng = settingsDraft.geofencingLocation?.coordinates?.lng ?? null;
                            setSettingsDraft(prev => ({ ...prev, geofencingLocation: { ...(prev.geofencingLocation || {}), coordinates: { lat: nextLat, lng: curLng } } }));
                          }}
                          style={inputStyle}
                          disabled={!settingsDraft.geofencingEnabled}
                        />
                      </div>
                      <div>
                        <div style={labelStyle}>Longitude</div>
                        <input
                          type="number"
                          placeholder="e.g. 77.5946"
                          value={settingsDraft.geofencingLocation?.coordinates?.lng ?? ''}
                          onChange={(e) => {
                            const nextLng = e.target.value === '' ? null : Number(e.target.value);
                            const curLat = settingsDraft.geofencingLocation?.coordinates?.lat ?? null;
                            setSettingsDraft(prev => ({ ...prev, geofencingLocation: { ...(prev.geofencingLocation || {}), coordinates: { lat: curLat, lng: nextLng } } }));
                          }}
                          style={inputStyle}
                          disabled={!settingsDraft.geofencingEnabled}
                        />
                      </div>
                    </div>

                    <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${colors.border}`, background: colors.weekend }}>
                      <div style={{ height: 260, width: '100%' }}>
                        <MapContainer
                          key={`${geofenceMapKey}-${settingsDraft.geofencingEnabled ? 'on' : 'off'}`}
                          center={mapCenter}
                          zoom={hasCoords ? 15 : 4}
                          scrollWheelZoom={false}
                          style={{ height: '100%', width: '100%' }}
                        >
                          <TileLayer
                            attribution="&copy; OpenStreetMap contributors"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <GeofenceClickSetter enabled={!!settingsDraft.geofencingEnabled} />
                          {hasCoords && (
                            <Marker
                              position={[Number(lat), Number(lng)]}
                              draggable={!!settingsDraft.geofencingEnabled}
                              eventHandlers={{
                                dragend: (e) => {
                                  const p = e?.target?.getLatLng?.();
                                  if (!p) return;
                                  const nextLat = p.lat;
                                  const nextLng = p.lng;
                                  setSettingsDraft(prev => ({
                                    ...prev,
                                    geofencingLocation: {
                                      ...(prev.geofencingLocation || {}),
                                      coordinates: { lat: nextLat, lng: nextLng }
                                    }
                                  }));
                                  geofenceReverseGeocode({ lat: nextLat, lng: nextLng }).then((r) => {
                                    if (r?.success && r.address) {
                                      setSettingsDraft(prev => ({
                                        ...prev,
                                        geofencingLocation: {
                                          ...(prev.geofencingLocation || {}),
                                          address: r.address,
                                          coordinates: { lat: nextLat, lng: nextLng }
                                        }
                                      }));
                                    }
                                  }).catch(() => {});
                                }
                              }}
                            >
                              <Popup>
                                <div style={{ fontSize: 12 }}>
                                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Geofence center</div>
                                  <div>{Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}</div>
                                  {!!settingsDraft.geofencingLocation?.address && (
                                    <div style={{ marginTop: 6 }}>{settingsDraft.geofencingLocation.address}</div>
                                  )}
                                </div>
                              </Popup>
                            </Marker>
                          )}
                        </MapContainer>
                      </div>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <div style={labelStyle}>Office address (optional)</div>
                      <input
                        type="text"
                        placeholder="Address label to show in UI"
                        value={settingsDraft.geofencingLocation?.address || ''}
                        onChange={(e) => setSettingsDraft(prev => ({ ...prev, geofencingLocation: { ...(prev.geofencingLocation || {}), address: e.target.value } }))}
                        style={inputStyle}
                        disabled={!settingsDraft.geofencingEnabled}
                      />
                      {hasCoords && (
                        <div style={{ marginTop: 8 }}>
                          <a
                            href={`https://www.google.com/maps?q=${Number(lat)},${Number(lng)}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12, fontWeight: 800, color: colors.today, textDecoration: 'none' }}
                          >
                            Open pinned location in Maps
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                {/* Intentionally left in grid for future fields; kept to preserve layout spacing */}
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: 16, borderTop: `1px solid ${colors.border}`, background: '#fff', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <button
            onClick={() => onReset?.()}
            disabled={disabled}
            style={{ border: `1px solid ${colors.border}`, background: '#fff', color: colors.textMuted, padding: '10px 12px', borderRadius: 12, cursor: 'pointer', fontWeight: 800, fontSize: 12 }}
          >
            Reset changes
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              disabled={disabled}
              style={{ border: `1px solid ${colors.border}`, background: '#fff', color: colors.textMuted, padding: '10px 12px', borderRadius: 12, cursor: 'pointer', fontWeight: 800, fontSize: 12 }}
            >
              Cancel
            </button>
            <button
              onClick={() => onSave?.()}
              disabled={disabled || (!isDirty && isPersisted)}
              style={{ border: 'none', background: colors.today, color: '#fff', padding: '10px 14px', borderRadius: 12, cursor: (disabled || (!isDirty && isPersisted)) ? 'not-allowed' : 'pointer', fontWeight: 900, fontSize: 12, opacity: (disabled || (!isDirty && isPersisted)) ? 0.7 : 1 }}
            >
              {isSaving ? 'Saving…' : (!isDirty && isPersisted ? 'Saved' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceSettingsModal;


